import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faArrowLeft,
  faTimes,
  faEye,
  faDownload,
  faUpload,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileImage,
  faFileAlt,
  faExclamationTriangle,
  faPencilAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  getCabinetMinistre,
  getCabinetMinistreById,
  createCabinetMinistre,
  updateCabinetMinistre,
  deleteCabinetMinistre,
} from "../../../services/api";
import { CabinetMinistre } from "../../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "../AdminLayout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation } from "../../../utils/translations";
import {
  getFileExtension,
  getFileName,
  isImageFile,
  isPdfFile,
  resolveMediaUrl,
} from "../../../utils/media";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

interface FileWithInfo {
  name: string;
  size: number;
  type: string;
  preview?: string;
  originalFile?: File;
}

const CabinetMinistreForm: React.FC = () => {
  const { language } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<FileWithInfo | null>(null);
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const [existingFileType, setExistingFileType] = useState<string>("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [arabicDesc, setArabicDesc] = useState("");
  const [frenchDesc, setFrenchDesc] = useState("");
  const [existingRecords, setExistingRecords] = useState<number>(0);
  const [isCheckingRecords, setIsCheckingRecords] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CabinetMinistre>();

  useEffect(() => {
    const checkExistingRecords = async () => {
      try {
        const response = await getCabinetMinistre();
        setExistingRecords(response.data.length);
      } catch (error) {
        console.error("Error checking existing records:", error);
      } finally {
        setIsCheckingRecords(false);
      }
    };

    checkExistingRecords();
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          const response = await getCabinetMinistreById(parseInt(id));
          const data = response.data;

          reset({
            ...data,
          });
          setArabicDesc(data.descarabe || "");
          setFrenchDesc(data.descfrancais || "");
          if (data.fichier) {
            setExistingFile(data.fichier);
            const extension = getFileExtension(data.fichier);
            if (extension === "pdf") setExistingFileType("application/pdf");
            else if (["jpg", "jpeg"].includes(extension))
              setExistingFileType("image/jpeg");
            else if (extension === "png") setExistingFileType("image/png");
            else if (extension === "gif") setExistingFileType("image/gif");
            else if (extension === "webp") setExistingFileType("image/webp");
          }
        } catch (error) {
          console.error("Error fetching cabinet item:", error);
          toast.error(getTranslation("fetchError", language));
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [id, isEditMode, reset]);

  const handleFileDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files && files[0]) {
        handleFileUpload(files[0]);
      }
      setIsDragOver(false);
    },
    [],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = (uploadedFile: File) => {
    if (uploadedFile.size > 10 * 1024 * 1024) {
      toast.error("حجم الملف كبير جداً. الحد الأقصى 10 ميجابايت");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowedTypes.includes(uploadedFile.type)) {
      toast.error(
        "نوع الملف غير مدعوم. الرجاء رفع ملف PDF، صورة، Word أو Excel",
      );
      return;
    }

    const fileWithInfo: FileWithInfo = {
      name: uploadedFile.name,
      size: uploadedFile.size,
      type: uploadedFile.type,
      originalFile: uploadedFile,
    };

    // Generate preview for images
    if (uploadedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        fileWithInfo.preview = e.target?.result as string;
        setFile(fileWithInfo);
        // Clear existing file when uploading new one
        setExistingFile(null);
        setExistingFileType("");
      };
      reader.readAsDataURL(uploadedFile);
    } else {
      setFile(fileWithInfo);
      // Clear existing file when uploading new one
      setExistingFile(null);
      setExistingFileType("");
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  const removeExistingFile = () => {
    setExistingFile(null);
    setExistingFileType("");
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes("pdf")) return faFilePdf;
    if (fileType.includes("word") || fileType.includes("document"))
      return faFileWord;
    if (fileType.includes("excel") || fileType.includes("sheet"))
      return faFileExcel;
    if (
      fileType.includes("image/jpeg") ||
      fileType.includes("image/png") ||
      fileType.includes("image/gif") ||
      fileType.includes("image/webp")
    )
      return faFileImage;
    return faFileAlt;
  };

  const getFileIconFromExisting = () => {
    if (existingFile) {
      if (isPdfFile(existingFile)) return faFilePdf;
      if (isImageFile(existingFile)) return faFileImage;
    }
    if (existingFileType.includes("pdf")) return faFilePdf;
    if (
      existingFileType.includes("word") ||
      existingFileType.includes("document")
    )
      return faFileWord;
    if (
      existingFileType.includes("excel") ||
      existingFileType.includes("sheet")
    )
      return faFileExcel;
    if (
      existingFileType.includes("image/jpeg") ||
      existingFileType.includes("image/png") ||
      existingFileType.includes("image/gif") ||
      existingFileType.includes("image/webp")
    )
      return faFileImage;
    return faFileAlt;
  };

  const handleViewFile = () => {
    if (existingFile) {
      window.open(resolveMediaUrl(existingFile), "_blank");
    }
  };

  const handleDownloadFile = () => {
    if (existingFile) {
      const link = document.createElement("a");
      link.href = resolveMediaUrl(existingFile);
      link.download = getFileName(existingFile) || "cabinet-file";
      link.click();
    }
  };

  const encodeFileContent = (fileInfo: FileWithInfo): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!fileInfo.originalFile) {
        reject(new Error("No file found"));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(fileInfo.originalFile);
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const encodedContent = dataUrl;
        resolve(encodedContent);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data: CabinetMinistre) => {
    if (!isEditMode && existingRecords > 0) {
      toast.error(
        "يوجد سجل واحد فقط لمكتب الوزير. الرجاء تعديل السجل الموجود أو حذفه أولاً.",
      );
      return;
    }

    setIsLoading(true);
    try {
      let fileValue: string | null = null;
      let fileType: string = "";

      if (file) {
        fileValue = await encodeFileContent(file);
        fileType = file.type;
      } else if (existingFile && isEditMode) {
        fileValue = getFileName(existingFile) || existingFile;
        fileType = existingFileType;
      }

      const payload = {
        titrearabe: data.titrearabe,
        titrefrancais: data.titrefrancais,
        descarabe: arabicDesc,
        descfrancais: frenchDesc,
        fichier: fileValue,
        fichier_type: fileType,
      };

      if (isEditMode) {
        await updateCabinetMinistre(parseInt(id!), payload);
        toast.success("تم تحديث السجل بنجاح");
      } else {
        await createCabinetMinistre(payload);
        toast.success("تمت إضافة السجل بنجاح");
      }

      navigate("/admin/cabinetministre");
    } catch (error) {
      console.error("Error saving cabinet item:", error);
      toast.error(getTranslation("saveError", language));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      await deleteCabinetMinistre(parseInt(id));
      toast.success("تم حذف السجل بنجاح");
      navigate("/admin/cabinetministre");
    } catch (error) {
      console.error("Error deleting cabinet item:", error);
      toast.error("حدث خطأ أثناء حذف السجل");
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getFileSize = (size: number) => {
    if (size < 1024) return size + " bytes";
    else if (size < 1024 * 1024) return (size / 1024).toFixed(1) + " KB";
    else return (size / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileTypeName = (type: string) => {
    if (type.includes("pdf")) return "PDF";
    if (type.includes("jpeg") || type.includes("jpg")) return "صورة JPEG";
    if (type.includes("png")) return "صورة PNG";
    if (type.includes("gif")) return "صورة GIF";
    if (type.includes("webp")) return "صورة WebP";
    if (type.includes("word")) return "وثيقة Word";
    if (type.includes("excel")) return "ملف Excel";
    return "ملف";
  };

  const renderFilePreview = () => {
    if (file && file.preview) {
      // New file preview (image)
      return (
        <div className="flex flex-col items-center">
          <div className="relative">
            <img
              src={file.preview}
              alt="Preview"
              className="h-32 w-32 object-cover rounded-lg mb-2"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 w-6 h-6 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
          <p className="text-sm font-medium text-gray-700 mb-1 truncate max-w-full">
            {file.name}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{getFileTypeName(file.type || "")}</span>
            <span>•</span>
            <span>{getFileSize(file.size)}</span>
          </div>
        </div>
      );
    } else if (file) {
      // New file preview (non-image)
      return (
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="bg-gray-100 p-4 rounded-lg mb-2 h-32 w-32 flex items-center justify-center">
              <FontAwesomeIcon
                icon={getFileIcon(file.type || "")}
                className="text-mauritania-green text-3xl"
              />
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeFile();
              }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 w-6 h-6 flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
          <p className="text-sm font-medium text-gray-700 mb-1 truncate max-w-full">
            {file.name}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{getFileTypeName(file.type || "")}</span>
            <span>•</span>
            <span>{getFileSize(file.size)}</span>
          </div>
        </div>
      );
    } else if (existingFile) {
      // Existing file preview
      const displayUrl = resolveMediaUrl(existingFile);

      return (
        <div className="flex flex-col items-center">
          <div className="relative">
            {isImageFile(existingFile) || existingFileType.startsWith("image/") ? (
              <div className="relative h-32 w-32 rounded-lg overflow-hidden mb-2 border border-gray-200">
                <img
                  src={displayUrl}
                  alt="Existing file"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    // If image fails to load, show file icon instead
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    target.parentElement?.classList.add(
                      "flex",
                      "items-center",
                      "justify-center",
                    );
                    target.parentElement!.innerHTML = `
                      <div class="text-center">
                        <svg class="text-mauritania-green w-12 h-12 mb-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
                        </svg>
                        <p class="text-xs text-gray-600">تعذر عرض الصورة</p>
                      </div>
                    `;
                  }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeExistingFile();
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 w-6 h-6 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="bg-gray-100 p-4 rounded-lg mb-2 h-32 w-32 flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={getFileIconFromExisting()}
                    className="text-mauritania-green text-3xl"
                  />
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeExistingFile();
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 w-6 h-6 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </div>
            )}
          </div>
          <p className="text-sm font-medium text-green-700 mb-2">ملف موجود</p>
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleViewFile();
              }}
              className="text-mauritania-green hover:text-mauritania-green-dark text-sm"
            >
              <FontAwesomeIcon icon={faEye} className="ml-1" />
              {getTranslation("preview", language)}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadFile();
              }}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              <FontAwesomeIcon icon={faDownload} className="ml-1" />
              {getTranslation("download", language)}
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  if (isCheckingRecords) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mauritania-green"></div>
          <p className="mt-2 text-gray-600">جاري التحقق من السجلات...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <style>
        {`
          .rtl-quill .ql-editor {
            direction: rtl;
            text-align: right;
          }
          
          .rtl-quill .ql-editor ol, 
          .rtl-quill .ql-editor ul {
            padding-right: 1.5em;
            padding-left: 0;
          }
          
          .rtl-quill .ql-editor li:before {
            margin-left: 0.3em;
            margin-right: -1.5em;
          }
          
          .ltr-quill .ql-editor {
            direction: ltr;
            text-align: left;
          }
          
          .ltr-quill .ql-editor ol, 
          .ltr-quill .ql-editor ul {
            padding-left: 1.5em;
            padding-right: 0;
          }
        `}
      </style>

      {/* Warning message when trying to create new record when one exists */}
      {!isEditMode && existingRecords > 0 && (
        <div className="mb-6 bg-yellow-50 border-r-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-yellow-400"
              />
            </div>
            <div className="mr-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                يوجد سجل واحد بالفعل
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                يسمح بسجل واحد فقط لمكتب الوزير. إذا كنت تريد إضافة سجل جديد،
                الرجاء{" "}
                <Link
                  to="/admin/cabinetministre"
                  className="font-semibold underline"
                >
                  حذف السجل الموجود أولاً
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark">
          {isEditMode ? "تعديل السجل" : "إضافة سجل جديد"}
        </h1>
        <div className="flex gap-3">
          {isEditMode && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-300 flex items-center"
            >
              <FontAwesomeIcon icon={faTrashAlt} className="ml-2" />
              {isDeleting ? "جاري الحذف..." : "حذف السجل"}
            </button>
          )}
          <button
            onClick={() => navigate("/admin/cabinetministre")}
            className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300 flex items-center"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />
            {getTranslation("back", language)}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 bg-red-100 p-3 rounded-full">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="text-red-600 text-xl"
                  />
                </div>
                <div className="mr-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    تأكيد الحذف
                  </h3>
                  <p className="text-gray-600 mt-1">
                    هل أنت متأكد من حذف هذا السجل؟ هذا الإجراء لا يمكن التراجع
                    عنه.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 space-x-reverse">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition duration-300"
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition duration-300"
                >
                  {isDeleting ? "جاري الحذف..." : "نعم، احذف"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Titles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="titrearabe">
                العنوان (عربي) *
              </label>
              <input
                id="titrearabe"
                type="text"
                {...register("titrearabe", { required: "هذا الحقل مطلوب" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrearabe ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrearabe && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrearabe.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="titrefrancais"
              >
                العنوان (فرنسي) *
              </label>
              <input
                id="titrefrancais"
                type="text"
                {...register("titrefrancais", { required: "هذا الحقل مطلوب" })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrefrancais ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titrefrancais && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrefrancais.message}
                </p>
              )}
            </div>
          </div>

          {/* Arabic Description */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">الوصف (عربي)</label>
            <div className="rtl-quill">
              <ReactQuill
                theme="snow"
                value={arabicDesc}
                onChange={setArabicDesc}
                modules={quillModules}
                className="h-64 mb-16"
              />
            </div>
          </div>

          {/* French Description */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">الوصف (فرنسي)</label>
            <div className="ltr-quill">
              <ReactQuill
                theme="snow"
                value={frenchDesc}
                onChange={setFrenchDesc}
                modules={quillModules}
                className="h-64 mb-16"
              />
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">الملف المرفق</label>

            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? "border-mauritania-green bg-mauritania-green-light"
                  : "border-gray-300 hover:border-mauritania-green"
              }`}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <input
                id="file-input"
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx"
              />

              {file || existingFile ? (
                renderFilePreview()
              ) : (
                <div>
                  <div className="bg-gray-100 p-3 rounded-lg inline-block mb-3">
                    <FontAwesomeIcon
                      icon={faUpload}
                      className="text-gray-400 text-2xl"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    اسحب الملف هنا أو انقر للاختيار
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, صور, Word, Excel (الحد الأقصى: 10 ميجابايت)
                  </p>
                </div>
              )}
            </div>

            {isEditMode && !file && existingFile && (
              <p className="text-sm text-gray-500 mt-2">
                انقر على أيقونة (✕) لإزالة الملف الحالي
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/cabinetministre")}
              className="bg-gray-300 text-gray-700 py-2 px-6 rounded-md hover:bg-gray-400 transition duration-300"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isLoading || (!isEditMode && existingRecords > 0)}
              className="bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50 disabled:opacity-50 flex items-center"
            >
              <FontAwesomeIcon
                icon={isEditMode ? faPencilAlt : faSave}
                className="ml-2"
              />
              {isLoading
                ? getTranslation("saving", language)
                : isEditMode
                  ? "تعديل السجل"
                  : "إضافة السجل"}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default CabinetMinistreForm;
